// What an audit says about the procedures it was done with. Server read models: the tab shows them
// and posts back what the lead auditor picked, so none of these is ever dirty-checked - except the
// criterion drafts, whose proposed texts the user edits before approving them.

// Why a picked remark produced no criterion draft. Cross-repo contract with InternalAuditManager.
export const InternalAuditImprovementSkipReasons = {
  notACriterion: 'NotACriterion',
  noLongerExists: 'NoLongerExists',
  overCap: 'OverCap',
  modelUnavailable: 'ModelUnavailable',
} as const;

export class InternalAuditComment {
  taskId: number;

  taskName: string;

  procedureTaskId?: number;

  procedureName?: string;

  coveredItemNames: string[];

  kpiId: number;

  question: string;

  // the remark as plain text (what the model reads) and as the HTML the auditee wrote (what is shown)
  comment: string;

  commentHtml: string;

  // The audit criterion the question was generated from, when it was one: the entity that owns the
  // criterion and the criterion's key. Absent on a question the procedure's form asks on its own -
  // such a remark can become a task, never a criterion change.
  entityType?: number;

  entityId?: number;

  criterionKey?: string;

  entityName?: string;

  constructor() {
    this.taskId = 0;
    this.taskName = '';
    this.coveredItemNames = [];
    this.kpiId = 0;
    this.question = '';
    this.comment = '';
    this.commentHtml = '';
  }

  // The comment and the question it was written beside are one thing in the list: this is the key
  // that says which row a selection is about.
  key(): string {
    return `${this.taskId}-${this.kpiId}`;
  }

  hasCriterion(): boolean {
    return this.entityType !== undefined && this.entityId !== undefined && !!this.criterionKey;
  }
}

export class InternalAuditUnmatchedTask {
  taskId: number;

  taskName: string;

  description: string;

  coveredItemNames: string[];

  // The name the procedure made from this task would carry
  procedureName: string;

  // Set when the library already holds a procedure under that name: applying improves that one
  // rather than adding a second, and the two flags say what would move
  existingProcedureTaskId?: number;

  existingProcedureName?: string;

  descriptionDiffers: boolean;

  checkListDiffers: boolean;

  constructor() {
    this.taskId = 0;
    this.taskName = '';
    this.description = '';
    this.coveredItemNames = [];
    this.procedureName = '';
    this.descriptionDiffers = false;
    this.checkListDiffers = false;
  }

  // Nothing to bring over: the procedure already asks exactly this, so applying only links the task
  isSameAsExistingProcedure(): boolean {
    return this.existingProcedureTaskId !== undefined && !this.descriptionDiffers && !this.checkListDiffers;
  }
}

export class InternalAuditImprovement {
  comments: InternalAuditComment[];

  unmatchedTasks: InternalAuditUnmatchedTask[];

  // Both happen once per audit: the improvement task that was created from the remarks (the button
  // then opens it instead of making a second), and the moment the criteria drafts were applied (a
  // second round would build on texts the auditors never saw).
  improvementTaskId?: number;

  criteriaImprovedOn?: Date;

  constructor() {
    this.comments = [];
    this.unmatchedTasks = [];
  }

  hasImprovementTask(): boolean {
    return this.improvementTaskId !== undefined;
  }

  isCriteriaImproved(): boolean {
    return this.criteriaImprovedOn !== undefined;
  }

  clone(): InternalAuditImprovement {
    const output = new InternalAuditImprovement();
    output.comments = this.comments;
    output.unmatchedTasks = this.unmatchedTasks;
    output.improvementTaskId = this.improvementTaskId;
    output.criteriaImprovedOn = this.criteriaImprovedOn;

    return output;
  }
}

export class InternalAuditImprovementProposal {
  taskId: number;

  kpiId: number;

  question: string;

  comment: string;

  commentHtml: string;

  procedureTaskId?: number;

  procedureName?: string;

  entityType?: number;

  entityId?: number;

  criterionKey?: string;

  entityName?: string;

  evaluation: string;

  recommendation: string;

  constructor() {
    this.taskId = 0;
    this.kpiId = 0;
    this.question = '';
    this.comment = '';
    this.commentHtml = '';
    this.evaluation = '';
    this.recommendation = '';
  }

  key(): string {
    return `${this.taskId}-${this.kpiId}`;
  }

  hasCriterion(): boolean {
    return this.entityType !== undefined && this.entityId !== undefined && !!this.criterionKey;
  }
}

export class InternalAuditImprovementAnalysis {
  proposals: InternalAuditImprovementProposal[];

  nothingToImprove: boolean;

  constructor() {
    this.proposals = [];
    this.nothingToImprove = false;
  }
}

// One proposed change to ONE audit criterion, drafted from every picked remark about it. The originals
// are the criterion's current texts; the proposal is the user's to edit before approving. `include` is
// the tick in the approval modal and never leaves the client.
export class InternalAuditCriterionDraft {
  entityType: number;

  entityId: number;

  entityName: string;

  criterionKey: string;

  originalRequirement: string;

  originalSuccessCriteria: string;

  proposedRequirement: string;

  proposedSuccessCriteria: string;

  remarks: InternalAuditImprovementProposal[];

  include: boolean;

  constructor() {
    this.entityType = 0;
    this.entityId = 0;
    this.entityName = '';
    this.criterionKey = '';
    this.originalRequirement = '';
    this.originalSuccessCriteria = '';
    this.proposedRequirement = '';
    this.proposedSuccessCriteria = '';
    this.remarks = [];
    this.include = true;
  }

  key(): string {
    return `${this.entityType}|${this.entityId}|${this.criterionKey}`;
  }

  // Half a criterion is no criterion: both texts have to be there before the draft can be approved.
  isValid(): boolean {
    return this.proposedRequirement.trim().length > 0 && this.proposedSuccessCriteria.trim().length > 0;
  }

  hasChange(): boolean {
    return (
      this.proposedRequirement.trim() !== this.originalRequirement.trim() ||
      this.proposedSuccessCriteria.trim() !== this.originalSuccessCriteria.trim()
    );
  }

  clone(): InternalAuditCriterionDraft {
    const output = new InternalAuditCriterionDraft();
    output.entityType = this.entityType;
    output.entityId = this.entityId;
    output.entityName = this.entityName;
    output.criterionKey = this.criterionKey;
    output.originalRequirement = this.originalRequirement;
    output.originalSuccessCriteria = this.originalSuccessCriteria;
    output.proposedRequirement = this.proposedRequirement;
    output.proposedSuccessCriteria = this.proposedSuccessCriteria;
    output.remarks = [...this.remarks];
    output.include = this.include;

    return output;
  }
}

// Picked remarks that produced no draft, and why (InternalAuditImprovementSkipReasons). They can
// still become a task.
export class InternalAuditCriterionSkipped {
  entityType?: number;

  entityId?: number;

  entityName?: string;

  criterionKey?: string;

  reason: string;

  remarks: InternalAuditImprovementProposal[];

  constructor() {
    this.reason = '';
    this.remarks = [];
  }
}

export class InternalAuditCriteriaPreview {
  drafts: InternalAuditCriterionDraft[];

  skipped: InternalAuditCriterionSkipped[];

  constructor() {
    this.drafts = [];
    this.skipped = [];
  }
}

// What one approved draft did. A criterion that already carried an answer is forked: the version the
// auditors answered stays archived, the change lives on under the next key of the lineage.
export class InternalAuditCriterionApplied {
  entityType: number;

  entityId: number;

  entityName?: string;

  oldCriterionKey: string;

  newCriterionKey: string;

  forked: boolean;

  coveringProcedureNames: string[];

  constructor() {
    this.entityType = 0;
    this.entityId = 0;
    this.oldCriterionKey = '';
    this.newCriterionKey = '';
    this.forked = false;
    this.coveringProcedureNames = [];
  }
}

export class InternalAuditCriteriaApplyResult {
  applied: InternalAuditCriterionApplied[];

  constructor() {
    this.applied = [];
  }

  procedureCount(): number {
    return new Set(this.applied.flatMap((item) => item.coveringProcedureNames)).size;
  }
}

export class InternalAuditImprovementResult {
  taskId?: number;

  createdProcedureNames: string[];

  // Existing procedures a task improved (instruction or checklist brought in line)
  updatedProcedureNames: string[];

  // Procedures a task was attached to without changing them: they already asked what it asks
  linkedProcedureNames: string[];

  constructor() {
    this.createdProcedureNames = [];
    this.updatedProcedureNames = [];
    this.linkedProcedureNames = [];
  }
}
